/**
 * lineupUtils.js — pure helpers for the live on-court / bench lineup.
 *
 * Lineup shape (one per side, saved under match_config.lineup.<participant>):
 *   {
 *     onCourt:     [playerId]  — order is the on-court slot order
 *     bench:       [playerId]
 *     starters:    [playerId]  — who started the match on court
 *     pairs:       { [subId]: starterId } — sub currently on court for that starter
 *     subsThisSet: number
 *     set:         number
 *     log:         [{ inId, outId, set, score, at, period, clockMs }]
 *   }
 */

const sameId = (a, b) => a != null && b != null && String(a) === String(b);
const has = (list, id) => (list || []).some((x) => sameId(x, id));
const playerId = (p) => p?.id ?? p?.userId;

const BENCH_TYPES = new Set(['substitute', 'reserve']);

/** Primary members start on court; substitutes and reserves start on the bench. */
export const buildInitialLineup = (players = []) => {
  const onCourt = [];
  const bench = [];
  players.forEach((p) => {
    const id = playerId(p);
    if (id == null) return;
    (BENCH_TYPES.has(p.memberType) ? bench : onCourt).push(id);
  });
  // A roster with no primaries would leave the court empty — play everyone instead.
  if (onCourt.length === 0) {
    onCourt.push(...bench.splice(0));
  }
  return { onCourt, bench, starters: [...onCourt], pairs: {}, subsThisSet: 0, set: 1, log: [] };
};

/**
 * Reconcile a saved lineup with the current roster: drop players who left the
 * roster and put new roster players on the bench. Returns null when unusable.
 */
export const reconcileLineup = (saved, players = []) => {
  if (!saved || !Array.isArray(saved.onCourt) || !Array.isArray(saved.bench)) return null;
  const rosterIds = players.map(playerId).filter((id) => id != null);
  const onRoster = (id) => has(rosterIds, id);
  const onCourt = saved.onCourt.filter(onRoster);
  if (onCourt.length === 0) return null;
  const bench = saved.bench.filter(onRoster);
  rosterIds.forEach((id) => {
    if (!has(onCourt, id) && !has(bench, id)) bench.push(id);
  });
  return {
    onCourt,
    bench,
    starters: Array.isArray(saved.starters) ? saved.starters : [...onCourt],
    pairs: saved.pairs && typeof saved.pairs === 'object' ? { ...saved.pairs } : {},
    subsThisSet: Number.isInteger(saved.subsThisSet) ? saved.subsThisSet : 0,
    set: Number.isInteger(saved.set) && saved.set > 0 ? saved.set : 1,
    log: Array.isArray(saved.log) ? saved.log : [],
  };
};

/** The sub currently standing in for this starter, or undefined. */
export const findSubFor = (lineup, starterId) =>
  Object.keys(lineup?.pairs || {}).find((subId) => sameId(lineup.pairs[subId], starterId));

/** The player on the other end of a sub ↔ starter pairing, or undefined. */
export const getPairedPlayerId = (lineup, id) => {
  if (!lineup?.pairs) return undefined;
  const starter = Object.keys(lineup.pairs).find((k) => sameId(k, id));
  if (starter !== undefined) return lineup.pairs[starter];
  const sub = findSubFor(lineup, id);
  return sub === undefined ? undefined : sub;
};

/** Substitutions made so far in the match (football: IFAB limit of 5). */
export const countSubstitutions = (lineup) => (lineup?.log || []).length;

/**
 * Can `inId` (bench) replace `outId` (on court)?
 * @returns {{ allowed: boolean, reason?: string }}
 */
export const canSubstitute = (lineup, outId, inId, rules = {}) => {
  if (!rules.enabled) return { allowed: false, reason: 'Substitutions are not used in this sport' };
  if (!lineup) return { allowed: false, reason: 'Lineup not loaded yet' };
  if (!has(lineup.onCourt, outId)) return { allowed: false, reason: 'That player is not on court' };
  if (!has(lineup.bench, inId)) return { allowed: false, reason: 'That player is not on the bench' };
  if (rules.maxPerSet != null && (lineup.subsThisSet || 0) >= rules.maxPerSet) {
    return { allowed: false, reason: `Substitution limit reached (${rules.maxPerSet} per set)` };
  }
  if (rules.maxPerMatch != null && countSubstitutions(lineup) >= rules.maxPerMatch) {
    return { allowed: false, reason: `Substitution limit reached (${rules.maxPerMatch} per match)` };
  }
  if (rules.noReentry && (lineup.log || []).some((e) => sameId(e?.outId, inId))) {
    return { allowed: false, reason: 'A player who was substituted off cannot come back on' };
  }
  if (rules.pairedReturn) {
    // A sub on court can only be replaced by the starter they came on for
    const starterOfOut = lineup.pairs?.[outId] ?? lineup.pairs?.[String(outId)];
    if (starterOfOut != null && !sameId(starterOfOut, inId)) {
      return { allowed: false, reason: 'A substitute can only be replaced by the player they came on for' };
    }
    // A starter who was subbed off can only come back for their own sub
    const subOfIn = findSubFor(lineup, inId);
    if (subOfIn !== undefined && !sameId(subOfIn, outId)) {
      return { allowed: false, reason: 'This player can only come back on for their substitute' };
    }
  }
  return { allowed: true };
};

/**
 * Swap `outId` (court) for `inId` (bench). The incoming player takes the exact
 * slot of the outgoing one so the on-court order never jumps around.
 * @param {Object} meta  { set, score, at, period, clockMs } — clockMs is the match
 *                       clock within the period, when the sport has one (AS-B06)
 */
export const applySubstitution = (lineup, outId, inId, meta = {}) => {
  const onCourt = lineup.onCourt.map((id) => (sameId(id, outId) ? inId : id));
  const bench = lineup.bench.map((id) => (sameId(id, inId) ? outId : id));

  const pairs = { ...lineup.pairs };
  const outKey = Object.keys(pairs).find((k) => sameId(k, outId));
  const isReturn = outKey !== undefined && sameId(pairs[outKey], inId);
  if (outKey !== undefined) delete pairs[outKey];
  if (!isReturn) {
    // inId now stands in for outId; clear any older link pointing at inId
    Object.keys(pairs).forEach((k) => { if (sameId(pairs[k], inId)) delete pairs[k]; });
    pairs[inId] = outId;
  }

  return {
    ...lineup,
    onCourt,
    bench,
    pairs,
    subsThisSet: (lineup.subsThisSet || 0) + 1,
    log: [
      ...(lineup.log || []),
      {
        inId,
        outId,
        set: meta.set ?? lineup.set ?? 1,
        score: meta.score ?? null,
        at: meta.at ?? new Date().toISOString(),
        period: meta.period ?? meta.set ?? lineup.set ?? 1,
        clockMs: Number.isFinite(meta.clockMs) ? Math.max(0, Math.round(meta.clockMs)) : null,
      },
    ],
  };
};

/** Start a new set: the per-set substitution counter goes back to 0. */
export const resetForNewSet = (lineup, set) => ({
  ...lineup,
  subsThisSet: 0,
  set: set ?? (lineup.set || 1) + 1,
});

/** Starters plus everyone who ever came on during the match. */
export const getAppearedPlayerIds = (lineup) => {
  if (!lineup) return [];
  const out = [];
  const add = (id) => { if (id != null && !has(out, id)) out.push(id); };
  (lineup.starters || []).forEach(add);
  (lineup.onCourt || []).forEach(add);
  (lineup.log || []).forEach((e) => { add(e.inId); add(e.outId); });
  return out;
};
